/**
 * Installing the console, and the one hook the shell needs to offer it.
 *
 * Two deliberate limits:
 *
 *  - **The worker is registered only in a production build.** In `vite dev` a cache-first worker
 *    in front of the dev server is a day lost to a stale module; `import.meta.env.PROD` keeps it
 *    off. That does mean the PWA is only ever exercised against `vite preview` or a real deploy —
 *    which is why `pnpm e2e` runs the built bundle rather than the dev server.
 *  - **The install prompt is the browser's, not ours.** `beforeinstallprompt` fires only when the
 *    browser has decided the app is installable and the user has not already installed or
 *    dismissed it. There is no way to make it fire and no honest way to fake the button: a
 *    permanently visible "Install" that does nothing on Safari or in an installed window is worse
 *    than no button. So it is captured when offered and the Shell renders the control only then.
 *
 * Author: Danish Khan
 */

/** Chromium's install event. Not in lib.dom, because it is not in the standard. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<(available: boolean) => void>();

const publish = (): void => {
  for (const listener of listeners) listener(deferred !== null);
};

export function onInstallAvailability(listener: (available: boolean) => void): () => void {
  listeners.add(listener);
  listener(deferred !== null);
  return () => {
    listeners.delete(listener);
  };
}

/** Show the browser's own install dialog. Returns what the person chose. */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const event = deferred;
  if (!event) return 'unavailable';
  // One prompt per event: it cannot be replayed, so it is dropped whatever the answer was.
  deferred = null;
  publish();
  await event.prompt();
  const { outcome } = await event.userChoice;
  return outcome;
}

export function setupPwa(): void {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Without this the browser shows its own mini-infobar and never hands us the event.
    e.preventDefault();
    deferred = e as BeforeInstallPromptEvent;
    publish();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    publish();
  });

  if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return;
  window.addEventListener('load', () => {
    // A registration that fails is not worth a dialog — the console works without it, and the only
    // thing lost is the offline page. It is logged so a deploy serving `sw.js` with the wrong
    // content type is findable.
    navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch((err: unknown) => {
      console.warn('h1: the service worker did not register; the offline page will not be shown', err);
    });
  });
}
